import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useSelectEntry } from "./useSelectEntry";

interface UseFormDataProps<T extends Record<string, any>> {
    entry_id: string | undefined;
    form: UseFormReturn<T>;
    fields: (keyof T)[];
    transform?: Partial<Record<keyof T, (value: any) => any>>;
}

export function useFormData<T extends Record<string, any>>({ entry_id, form, fields, transform }: UseFormDataProps<T>) {
    const { data: entryData, isLoading } = useSelectEntry(entry_id);

    // Load form data when entry data is available
    useEffect(() => {
        if (entryData && fields.length > 0) {
            fields.forEach((field) => {
                const rawValue = entryData[field as string];
                if (rawValue !== undefined && rawValue !== null) {
                    // Only set value if the field doesn't already have a value
                    const currentValue = form.getValues(field as any);
                    if (currentValue === undefined || currentValue === null) {
                        // Apply transform if provided, otherwise use raw value
                        const transformedValue = transform?.[field] ? transform[field]!(rawValue) : rawValue;

                        if (transformedValue !== undefined) {
                            form.setValue(field as any, transformedValue);
                        }
                    }
                }
            });
        }
    }, [entryData, form, fields, transform]);

    return {
        entryData,
        isLoading,
    };
}
